import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  PartyPopper,
} from "lucide-react";
import TurnstileWidget from "@/components/TurnstileWidget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LOAN_CATEGORIES, INDIAN_STATES } from "@/data/content";

const EMPLOYMENT_OPTS = [
  "Salaried",
  "Self-employed",
  // "Business Owner",
  "Professional",
];
const AMOUNT_OPTS = [
  "Under ₹10L",
  "₹10L – ₹25L",
  "₹25L – ₹50L",
  "₹50L – ₹1Cr",
  "₹1Cr – ₹5Cr",
  "Above ₹5Cr",
];
const INCOME_OPTS = [
  "Under ₹30K",
  "₹30K – ₹75K",
  "₹75K – ₹1.5L",
  "₹1.5L – ₹3L",
  "₹3L – ₹5L",
  "Above ₹5L",
];

const API = `${process.env.REACT_APP_BACKEND_URL}`;

const STEPS = [
  "Employment",
  "Loan Type",
  "Loan Amount",
  "Monthly Income",
  "City",
  "Your Details",
];

const EMPLOYMENT_TYPE_MAP = {
  Salaried: "SALARIED",
  "Self-employed": "SELF_EMPLOYED",
  // "Business Owner": "BUSINESS_OWNER",
  Professional: "PROFESSIONAL",
};

const LOAN_TYPE_MAP = {
  "Home Loan": "HOME_LOAN",
  "Business Loan": "BUSINESS_LOAN",
  "Loan Against Property": "LOAN_AGAINST_PROPERTY",
  "Loan Against Property (LAP)": "LOAN_AGAINST_PROPERTY",
  "Commercial Purchase Loan": "COMMERCIAL_PURCHASE_LOAN",
  "Mortgage Loan": "MORTGAGE_LOAN",
  "Construction Loan": "CONSTRUCTION_LOAN",
  "Overdraft / Dropline OD": "OD_DROPLINE_OD",
  "OD / Dropline OD": "OD_DROPLINE_OD",
  "CC Limit": "CC_LIMIT",
  "Car Loan": "CAR_LOAN",
  "Personal Loan": "PERSONAL_LOAN",
  "LRD Program": "LRD_PROGRAM",
  "Takeover / Top-Up": "TAKEOVER_TOPUP",
  "Takeover Top-Up": "TAKEOVER_TOPUP",
  "Takeover + Top-Up": "TAKEOVER_TOPUP",
  "Wealth Based Loan": "WEALTH_BASED_LOAN",
};

export default function EligibilityForm({ innerRef }) {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    employment: "",
    loan_type: "",
    loan_amount: "",
    monthly_income: "",
    state: "",
    city: "",
    name: "",
    phone: "",
    email: "",
    company_name: "",
    message: "",
    consent: false,
  });

  const [marketingData, setMarketingData] = useState({
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    utm_term: "",
    fbclid: "",
    gclid: "",
  });

  useEffect(() => {
    const hash = window.location.hash || "";

    /*
     * Supports URLs like:
     *
     * http://localhost:3000/#loans?utm_source=google&utm_medium=cpc
     *
     * Extract everything after the ? inside the hash.
     */
    const queryIndex = hash.indexOf("?");

    const hashQuery = queryIndex !== -1 ? hash.substring(queryIndex + 1) : "";

    const params = new URLSearchParams(hashQuery);

    const existing = JSON.parse(
      sessionStorage.getItem("moneymart_utm") || "{}",
    );

    const marketing = {
      utm_source: params.get("utm_source") || existing.utm_source || "",

      utm_medium: params.get("utm_medium") || existing.utm_medium || "",

      utm_campaign: params.get("utm_campaign") || existing.utm_campaign || "",

      utm_content: params.get("utm_content") || existing.utm_content || "",

      utm_term: params.get("utm_term") || existing.utm_term || "",

      fbclid: params.get("fbclid") || existing.fbclid || "",

      gclid: params.get("gclid") || existing.gclid || "",
    };

    setMarketingData(marketing);

    sessionStorage.setItem("moneymart_marketing", JSON.stringify(marketing));
  }, []);

  const getLeadSource = () => {
    if (marketingData.fbclid) {
      return "META";
    }

    if (marketingData.gclid) {
      return "GOOGLE";
    }

    return "WEBSITE";
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canProceed = () => {
    switch (step) {
      case 0:
        return !!data.employment;
      case 1:
        return !!data.loan_type;
      case 2:
        return !!data.loan_amount;
      case 3:
        return !!data.monthly_income;
      case 4:
        return !!data.state && data.city.trim().length >= 2;
      case 5:
        return (
          data.name.trim().length >= 2 &&
          /^\d{10}$/.test(data.phone) &&
          data.consent
        );
      default:
        return false;
    }
  };

  const buildLeadPayload = () => {
    return {
      fullName: data.name.trim(),
      phone: data.phone.trim(),
      email: data.email.trim() || null,
      state: data.state,
      city: data.city.trim(),
      loanType: LOAN_TYPE_MAP[data.loan_type],
      loanAmount: data.loan_amount.trim(),
      employmentType: EMPLOYMENT_TYPE_MAP[data.employment],
      monthlyIncome: data.monthly_income.trim(),
      companyName: data.company_name.trim() || null,
      message: data.message.trim() || null,
      status: "NEW",
      priority: "MEDIUM",
      source: getLeadSource(),
      assignedTo: null,
      followUpDate: null,
      utmSource: marketingData.utm_source || null,
      utmMedium: marketingData.utm_medium || null,
      utmCampaign: marketingData.utm_campaign || null,
      utmContent: marketingData.utm_content || null,
      utmTerm: marketingData.utm_term || null,
      fbclid: marketingData.fbclid || null,
      gclid: marketingData.gclid || null,

      turnstileToken,
      turnstileAction: "landing-lead",
    };
  };

  const submit = async () => {
    if (!canProceed()) return;
    const loanType = LOAN_TYPE_MAP[data.loan_type];
    const employmentType = EMPLOYMENT_TYPE_MAP[data.employment];

    if (!loanType) {
      toast.error("Submission failed", {
        description: "The selected loan type is not configured correctly.",
      });
      return;
    }

    if (!employmentType) {
      toast.error("Submission failed", {
        description:
          "The selected employment type is not configured correctly.",
      });
      return;
    }

    if (!turnstileToken) {
      toast.error("Please complete the security verification.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildLeadPayload();

      await axios.post(`${API}/leads`, payload);
      setDone(true);
      toast.success("Enquiry submitted", {
        description: "A Money Mart advisor will reach out shortly.",
      });
    } catch (e) {
      turnstileRef.current?.reset();
      const response = e?.response?.data;

      if (response?.errors?.length) {
        const errorMessage = response.errors
          .map((error) => error.message)
          .filter(Boolean)
          .join(", ");

        toast.error("Submission failed", {
          description: errorMessage,
        });
      } else {
        toast.error("Submission failed", {
          description:
            response?.message || "Something went wrong. Please try again.",
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="eligibility"
      ref={innerRef}
      data-testid="eligibility-form-section"
      className="relative py-20 md:py-28 overflow-hidden bg-[#F8FAFC]"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-24 right-10 w-72 h-72 rounded-full bg-[#1E5AA8]/5 blur-3xl" />
        <div className="absolute bottom-10 left-10 w-72 h-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-gray-500">
            Interactive Eligibility Checker
          </span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold font-heading tracking-tight text-[#0F2D52]">
            Check your eligibility in under a minute
          </h2>
          <p className="mt-3 text-sm text-gray-600 max-w-xl mx-auto">
            Share a few details — our advisor will match your profile with
            suitable partner banks. Approval is subject to the lender&rsquo;s
            policy.
          </p>
        </div>

        <div className="rounded-3xl bg-white border border-gray-200 shadow-[0_20px_60px_rgba(15,45,82,0.10)] p-6 sm:p-8 md:p-10">
          {done ? (
            <SuccessView />
          ) : (
            <>
              <div className="flex items-center justify-between mb-2 text-xs">
                <span className="font-semibold text-[#0F2D52]">
                  Step {step + 1} of {STEPS.length} · {STEPS[step]}
                </span>
                <span className="text-gray-500">{Math.round(progress)}%</span>
              </div>
              <Progress
                value={progress}
                data-testid="form-progress"
                className="h-1.5 bg-gray-100 [&>*]:bg-[#0F2D52]"
              />

              <div className="mt-8 min-h-[220px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.25 }}
                  >
                    {step === 0 && (
                      <OptionGrid
                        label="What best describes your employment?"
                        options={EMPLOYMENT_OPTS}
                        value={data.employment}
                        onSelect={(v) => {
                          set("employment", v);
                          setTimeout(next, 150);
                        }}
                        testPrefix="employment"
                      />
                    )}
                    {step === 1 && (
                      <OptionGrid
                        label="Which loan are you interested in?"
                        options={LOAN_CATEGORIES.map((c) => c.title)}
                        value={data.loan_type}
                        onSelect={(v) => {
                          set("loan_type", v);
                          setTimeout(next, 150);
                        }}
                        testPrefix="loan-type"
                        cols={3}
                      />
                    )}
                    {step === 2 && (
                      <OptionGrid
                        label="How much loan amount are you looking for?"
                        options={AMOUNT_OPTS}
                        value={data.loan_amount}
                        onSelect={(v) => {
                          set("loan_amount", v);
                          setTimeout(next, 150);
                        }}
                        testPrefix="loan-amount"
                        cols={3}
                      />
                    )}
                    {step === 3 && (
                      <OptionGrid
                        label="What is your monthly income (net)?"
                        options={INCOME_OPTS}
                        value={data.monthly_income}
                        onSelect={(v) => {
                          set("monthly_income", v);
                          setTimeout(next, 150);
                        }}
                        testPrefix="monthly-income"
                        cols={3}
                      />
                    )}
                    {step === 4 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label
                            htmlFor="input-state"
                            className="text-sm font-semibold text-[#0F2D52] mb-2 block"
                          >
                            State
                          </Label>

                          <Select
                            value={data.state}
                            onValueChange={(value) => set("state", value)}
                          >
                            <SelectTrigger
                              id="input-state"
                              data-testid="input-state"
                              className="h-12 w-full rounded-xl border-gray-300 bg-white px-4 text-sm focus:border-[#1E5AA8] focus:ring-[#1E5AA8]"
                            >
                              <SelectValue placeholder="Select your state" />
                            </SelectTrigger>

                            <SelectContent>
                              {INDIAN_STATES.map((state) => (
                                <SelectItem key={state} value={state}>
                                  {state}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label
                            htmlFor="input-city"
                            className="text-sm font-semibold text-[#0F2D52] mb-2 block"
                          >
                            City
                          </Label>

                          <Input
                            id="input-city"
                            data-testid="input-city"
                            placeholder="e.g. Jodhpur"
                            value={data.city}
                            onChange={(e) => set("city", e.target.value)}
                            className="h-12 rounded-xl border-gray-300 focus:border-[#1E5AA8] focus-visible:ring-[#1E5AA8]"
                          />
                        </div>
                      </div>
                    )}
                    {step === 5 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">
                          <Label className="text-sm font-semibold text-[#0F2D52] mb-2 block">
                            Full name
                          </Label>
                          <Input
                            data-testid="input-name"
                            placeholder="e.g. Ananya Sharma"
                            value={data.name}
                            onChange={(e) => set("name", e.target.value)}
                            className="h-12 rounded-xl border-gray-300 focus:border-[#1E5AA8] focus-visible:ring-[#1E5AA8]"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-semibold text-[#0F2D52] mb-2 block">
                            Mobile number
                          </Label>
                          <Input
                            data-testid="input-phone"
                            inputMode="numeric"
                            maxLength={10}
                            placeholder="10-digit mobile"
                            value={data.phone}
                            onChange={(e) =>
                              set(
                                "phone",
                                e.target.value.replace(/\D/g, "").slice(0, 10),
                              )
                            }
                            className={`h-12 rounded-xl focus:border-[#1E5AA8] focus-visible:ring-[#1E5AA8] ${
                              data.phone.length > 0 &&
                              !/^\d{10}$/.test(data.phone)
                                ? "border-red-500 focus:border-red-500 focus-visible:ring-red-500"
                                : "border-gray-300"
                            }`}
                          />
                          {data.phone.length > 0 &&
                            !/^\d{10}$/.test(data.phone) && (
                              <p className="mt-1.5 text-sm text-red-600">
                                A valid 10-digit mobile number is required.
                              </p>
                            )}
                        </div>
                        <div>
                          <Label className="text-sm font-semibold text-[#0F2D52] mb-2 block">
                            Email (optional)
                          </Label>
                          <Input
                            data-testid="input-email"
                            type="email"
                            placeholder="you@example.com"
                            value={data.email}
                            onChange={(e) => set("email", e.target.value)}
                            className="h-12 rounded-xl border-gray-300 focus:border-[#1E5AA8] focus-visible:ring-[#1E5AA8]"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label
                            htmlFor="input-company-name"
                            className="text-sm font-semibold text-[#0F2D52] mb-2 block"
                          >
                            Company Name (optional)
                          </Label>

                          <Input
                            id="input-company-name"
                            data-testid="input-company-name"
                            placeholder="Enter your company name"
                            value={data.company_name}
                            onChange={(e) =>
                              set("company_name", e.target.value)
                            }
                            className="h-12 rounded-xl border-gray-300 focus:border-[#1E5AA8] focus-visible:ring-[#1E5AA8]"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label
                            htmlFor="input-message"
                            className="text-sm font-semibold text-[#0F2D52] mb-2 block"
                          >
                            Message (optional)
                          </Label>

                          <textarea
                            id="input-message"
                            data-testid="input-message"
                            rows={4}
                            maxLength={1000}
                            placeholder="Tell us anything else you would like our advisor to know"
                            value={data.message}
                            onChange={(e) => set("message", e.target.value)}
                            className="w-full resize-none rounded-xl border border-gray-300 px-4 py-3 text-sm outline-none transition focus:border-[#1E5AA8] focus:ring-1 focus:ring-[#1E5AA8]"
                          />

                          <div className="mt-1 text-right text-xs text-gray-400">
                            {data.message.length}/1000
                          </div>
                        </div>
                        <div className="sm:col-span-2 flex items-start gap-3 pt-2">
                          <Checkbox
                            id="consent"
                            data-testid="input-consent"
                            checked={data.consent}
                            onCheckedChange={(v) => set("consent", !!v)}
                            className="mt-0.5 border-gray-400 data-[state=checked]:bg-[#0F2D52] data-[state=checked]:border-[#0F2D52]"
                          />
                          <label
                            htmlFor="consent"
                            className="text-xs text-gray-600 leading-relaxed cursor-pointer"
                          >
                            I authorize Money Mart to contact me regarding my
                            enquiry and understand that my information may be
                            shared with relevant lending partners for processing
                            my request.
                          </label>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="mt-8 flex items-center justify-between gap-3">
                <Button
                  variant="ghost"
                  onClick={back}
                  disabled={step === 0}
                  data-testid="form-back"
                  className="rounded-full text-gray-600 hover:text-[#0F2D52]"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>

                {step < STEPS.length - 1 ? (
                  <Button
                    onClick={next}
                    disabled={!canProceed()}
                    data-testid="form-next"
                    className="rounded-full bg-[#0F2D52] hover:bg-[#0a1f38] text-white h-11 px-6 disabled:opacity-40"
                  >
                    Continue
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <>
                    <TurnstileWidget
                      ref={turnstileRef}
                      action="landing-lead"
                      onTokenChange={setTurnstileToken}
                    />
                    <Button
                      onClick={submit}
                      disabled={!canProceed() || submitting || !turnstileToken}
                      data-testid="form-submit"
                      className="rounded-full bg-[#0F2D52] hover:bg-[#0a1f38] text-white h-11 px-6 disabled:opacity-50"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />{" "}
                          Submitting…
                        </>
                      ) : (
                        <>
                          Check Eligibility{" "}
                          <ArrowRight className="ml-1 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        <p className="mt-4 text-xs text-gray-500 text-center max-w-2xl mx-auto">
          Approval, interest rate, fees and tenure are determined solely by the
          respective Bank/NBFC. Submitting this enquiry does not guarantee
          sanction or disbursal.
        </p>
      </div>
    </section>
  );
}

function OptionGrid({ label, options, value, onSelect, testPrefix, cols = 2 }) {
  const colsClass = cols === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
  return (
    <div>
      <Label className="text-sm font-semibold text-[#0F2D52] mb-4 block">
        {label}
      </Label>
      <div className={`grid grid-cols-1 ${colsClass} gap-3`}>
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              data-testid={`${testPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              onClick={() => onSelect(opt)}
              className={`group text-left rounded-xl border p-4 mm-transition ${
                active
                  ? "border-[#0F2D52] bg-[#0F2D52]/5"
                  : "border-gray-200 hover:border-[#1E5AA8] hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span
                  className={`text-sm font-semibold ${active ? "text-[#0F2D52]" : "text-gray-800"}`}
                >
                  {opt}
                </span>
                {active && (
                  <div className="w-5 h-5 rounded-full bg-[#0F2D52] flex items-center justify-center">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function SuccessView() {
  return (
    <div data-testid="form-success" className="text-center py-6">
      <div className="w-16 h-16 rounded-2xl bg-[#16A34A]/10 flex items-center justify-center mx-auto mb-4">
        <PartyPopper className="h-7 w-7 text-[#16A34A]" />
      </div>
      <h3 className="text-2xl font-bold font-heading tracking-tight text-[#0F2D52]">
        Thank you — enquiry received
      </h3>
      <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
        A Money Mart advisor will reach out shortly on your registered mobile
        number. Approval remains subject to the respective lender&rsquo;s
        policy.
      </p>
      <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
        <a
          href="https://wa.me/919214417434"
          target="_blank"
          rel="noreferrer"
          data-testid="success-whatsapp"
          className="inline-flex items-center gap-2 rounded-full bg-[#16A34A] hover:bg-[#128a3a] text-white text-sm font-semibold h-11 px-5 mm-transition"
        >
          Message us on WhatsApp
        </a>
        <a
          href="#loans"
          data-testid="success-explore"
          className="inline-flex items-center gap-2 rounded-full border border-gray-300 text-[#0F2D52] hover:bg-gray-50 text-sm font-semibold h-11 px-5 mm-transition"
        >
          Explore loan solutions
        </a>
      </div>
    </div>
  );
}
